"use client";

import { createApi } from "@reduxjs/toolkit/query/react";

import baseQuery from "@/libs/shared/api/baseQuery";
import { objectToFormData } from "@/libs/shared/api/objectToFormData";
import {
  FeatureFlagsDataModel,
  FeatureFlagsModel,
  PasswordModel,
  PersonalDataModel,
  PreferencesModel,
  PreviewModel,
  ProfileModel,
  SubscriptionModel,
  SubscriptionPaddleModel,
  UpdatedBillingDetailsModel,
  UpdatedNotificationsModel,
} from "./settings.types";
import type { PaymentMethod } from "@stripe/stripe-js/dist/api/payment-methods";

const tagTypes = [
  "Profile" as const,
  "PersonalData" as const,
  "Password" as const,
  "Preferences" as const,
  "Notifications" as const,
  "Referral" as const,
  "Subscription" as const,
  "Subscriptions" as const,
  "BillingDetails" as const,
  "NextPayment" as const,
  "PaymentMethod" as const,
  "SetupStripe" as const,
  "FeatureFlags" as const,
  "ComeChatUserId" as const,
];

export const invalidateSettings = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api.queryFulfilled.then(() => {
      tags.forEach((tag) => {
        api.dispatch(settingsApi.util.invalidateTags([tag as any]));
      });
    });
  };
};

// Define a service using a base URL and expected endpoints
export const settingsApi = createApi({
  reducerPath: "settingsApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    getProfile: builder.query({
      query: () => ({
        url: `pt/account/profile`,
      }),
      providesTags: ["Profile"],
    }),
    getFeatureFlags: builder.query<FeatureFlagsModel, { params: string }>({
      query: ({ params }) => ({
        url: `pt/me${params}`,
      }),
      providesTags: ["FeatureFlags"],
    }),
    getPersonalData: builder.query({
      query: () => ({
        url: `pt/account/personal_data`,
      }),
      providesTags: ["PersonalData"],
    }),
    getPreferences: builder.query({
      query: () => ({
        url: `pt/account/preferences`,
      }),
      providesTags: ["Preferences"],
    }),
    getNotifications: builder.query({
      query: () => ({
        url: `pt/account/notifications`,
      }),
      providesTags: ["Notifications"],
    }),
    getReferral: builder.query({
      query: () => ({
        url: `pt/account/referral`,
      }),
      providesTags: ["Referral"],
    }),
    getSubscription: builder.query<{ data: SubscriptionModel }, undefined>({
      query: () => ({
        url: `pt/account/subscription`,
      }),
      providesTags: ["Subscription"],
    }),
    getPlan: builder.query({
      query: ({ id }: { id: string }) => ({
        url: `/pt/account/subscription/plans/${id}`,
      }),
    }),
    getPlans: builder.query({
      query: () => ({
        url: `/pt/account/subscription/plans`,
      }),
      providesTags: ["Subscriptions"],
    }),
    getNextPayment: builder.query({
      query: () => ({
        url: `pt/account/wallet/next_payment`,
      }),
      providesTags: ["NextPayment"],
    }),
    getBillingDetails: builder.query({
      query: () => ({
        url: `pt/account/billing_details`,
      }),
      providesTags: ["BillingDetails"],
    }),
    getPaymentMethod: builder.query({
      query: () => ({
        url: `/pt/account/payment_methods/default`,
      }),
      providesTags: ["PaymentMethod"],
    }),
    getCustomerPortal: builder.mutation<string, undefined>({
      query: () => ({
        url: `/pt/account/subscription/customer-portal`,
        method: "POST",
      }),
    }),
    deleteAccount: builder.mutation<void, { full_name: string }>({
      query: (body: { full_name: string }) => ({
        url: `/pt/account/deletion/request`,
        method: "POST",
        body,
      }),
    }),
    getChatEnabled: builder.query({
      query: () => ({
        url: `/pt/chat/user`,
      }),
      providesTags: ["ComeChatUserId"],
    }),
    updateProfile: builder.mutation<ProfileModel, { profile: ProfileModel }>({
      query: ({ profile }: { profile: ProfileModel }) => ({
        url: `pt/account/profile`,
        method: "POST",
        body: objectToFormData(profile),
      }),
      onQueryStarted: invalidateSettings(["Profile"]),
    }),
    updatePersonalData: builder.mutation({
      query: ({ personalData }: { personalData: PersonalDataModel }) => ({
        url: `pt/account/personal_data`,
        method: "POST",
        body: objectToFormData(personalData),
      }),
      onQueryStarted: invalidateSettings(["Profile", "PersonalData"]),
    }),
    updatePreferences: builder.mutation({
      query: ({ preferences }: { preferences: PreferencesModel }) => ({
        url: `pt/account/preferences`,
        method: "POST",
        body: objectToFormData(preferences),
      }),
      onQueryStarted: invalidateSettings(["Preferences"]),
    }),
    updatePassword: builder.mutation({
      query: ({ password }: { password: PasswordModel }) => ({
        url: `pt/account/password`,
        method: "POST",
        body: objectToFormData(password),
      }),
      onQueryStarted: invalidateSettings(["Password"]),
    }),
    updateNotifications: builder.mutation({
      query: ({
        updatedNotifications,
      }: {
        updatedNotifications: UpdatedNotificationsModel;
      }) => ({
        url: `pt/account/notifications`,
        method: "POST",
        body: updatedNotifications,
      }),
      onQueryStarted: invalidateSettings(["Notifications"]),
    }),
    updateBillingDetails: builder.mutation({
      query: ({
        updatedBillingDetails,
      }: {
        updatedBillingDetails: UpdatedBillingDetailsModel;
      }) => ({
        url: `pt/account/billing_details`,
        method: "POST",
        body: updatedBillingDetails,
      }),
      onQueryStarted: invalidateSettings(["BillingDetails"]),
    }),
    updatePaymentMethod: builder.mutation({
      query: ({
        paymentMethod,
        clientSecret,
      }: {
        paymentMethod: PaymentMethod;
        clientSecret: string;
      }) => ({
        url: `pt/account/payment_methods`,
        method: "POST",
        body: { ...paymentMethod, clientSecret },
      }),
      onQueryStarted: invalidateSettings(["PaymentMethod"]),
    }),
    updatePlan: builder.mutation({
      query: ({
        planData,
      }: {
        planData: { product_id: number; price_id: number };
      }) => ({
        url: `pt/account/subscription`,
        method: "POST",
        body: { ...planData },
      }),
      onQueryStarted: invalidateSettings(["Subscription"]),
    }),
    updateFeatureFlags: builder.mutation<
      FeatureFlagsModel,
      Partial<FeatureFlagsDataModel>
    >({
      query: (body) => ({
        url: `pt/account/contracts`,
        method: "POST",
        body,
      }),
      onQueryStarted: invalidateSettings(["FeatureFlags"]),
    }),
    createSubscription: builder.mutation<
      SubscriptionPaddleModel,
      { body: { price_id: number } }
    >({
      query: ({ body }) => ({
        url: `/pt/account/subscription/create`,
        method: "POST",
        body,
      }),
    }),
    updateSubscription: builder.mutation<
      SubscriptionPaddleModel,
      { body: { price_id: number } }
    >({
      query: ({ body }) => ({
        url: `/pt/account/subscription/update`,
        method: "PATCH",
        body,
      }),
    }),
    resetSubscription: builder.mutation({
      query: () => ({
        url: `/pt/account/subscription/restart`,
        method: "POST",
      }),
    }),
    previewSubscription: builder.mutation<
      PreviewModel,
      { body: { price_id: number } }
    >({
      query: ({ body }) => ({
        url: `/pt/account/subscription/preview`,
        method: "PATCH",
        body,
      }),
    }),
    setOneSignal: builder.mutation({
      query: (body: { oneSignalPlayerId: string; userId: string }) => ({
        url: `/update_one_signal_player_id`,
        method: "POST",
        body,
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetProfileQuery,
  useLazyGetFeatureFlagsQuery,
  useGetPersonalDataQuery,
  useGetPreferencesQuery,
  useGetNotificationsQuery,
  useGetReferralQuery,
  useGetSubscriptionQuery,
  useGetBillingDetailsQuery,
  useGetNextPaymentQuery,
  useGetPaymentMethodQuery,
  useGetCustomerPortalMutation,
  useGetPlanQuery,
  useGetPlansQuery,
  useGetChatEnabledQuery,
  useUpdateProfileMutation,
  useUpdatePersonalDataMutation,
  useUpdatePreferencesMutation,
  useUpdatePasswordMutation,
  useUpdateNotificationsMutation,
  useUpdateBillingDetailsMutation,
  useUpdatePaymentMethodMutation,
  useUpdatePlanMutation,
  useUpdateFeatureFlagsMutation,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  usePreviewSubscriptionMutation,
  useResetSubscriptionMutation,
  useDeleteAccountMutation,
  useSetOneSignalMutation,
} = settingsApi;
